import { Button, Stack, Typography } from "@mui/material";
import { filters } from "./utils/filters";
import Filter from "./components/Filter";
import { Add } from "@mui/icons-material";
import { useSearchParams } from "react-router-dom";
import { useCallback, useMemo, useState } from "react";
import { toNumber } from "src/utils/toNumber";
import {
  LANGUAGE,
  LIMIT,
  PAGE,
  PRODUCT,
  STATUS,
} from "src/constants/searchParams";
import { useGetPromoBannersQuery } from "src/api/services/bannersApi";
import NewBannerModal from "./components/NewBannerModal";
import DataTable from "src/components/DataTable";
import { useColumns } from "./utils/columns";
import { usePagination } from "src/hooks/usePagination";

const PromoBanners = () => {
  const [params] = useSearchParams();
  const [open, setOpen] = useState<boolean>(false);
  const columns = useColumns();
  const pagination = usePagination();

  const page = useMemo(() => toNumber(params.get(PAGE)), [params]);
  const limit = useMemo(() => toNumber(params.get(LIMIT)), [params]);

  const height = useMemo(() => window.innerHeight - 104 - 64 - 32 - 4, []);

  const from = useMemo(() => {
    return page * limit;
  }, [limit, page]);

  const to = useMemo(() => {
    return from + limit - 1;
  }, [limit, from]);

  const queryParams = useMemo(
    () => ({
      status: params.get(STATUS) ?? undefined,
      product: params.get(PRODUCT) ?? undefined,
      language: params.get(LANGUAGE) ?? undefined,
      from,
      to,
    }),
    [from, params, to],
  );

  const { data } = useGetPromoBannersQuery(queryParams);

  const { banners, count } = useMemo(() => {
    if (data == null) return { banners: [], count: 0 };

    return {
      banners: data.data.map((banner) => banner),
      count: data.count,
    };
  }, [data]);

  const handleOpenModal = useCallback(() => setOpen((o) => !o), []);

  return (
    <Stack gap="32px">
      <Typography variant="h4" fontWeight={600}>
        Flow Promo Banners
      </Typography>
      <Stack direction="row" gap={2}>
        <Button
          variant="outlined"
          startIcon={<Add />}
          onClick={handleOpenModal}
        >
          Create Banner
        </Button>
        {filters.map((filter) => (
          <Filter current={filter} key={filter.name} />
        ))}
      </Stack>
      <Stack gap={3} mt={3}>
        <DataTable
          columns={columns}
          data={banners}
          primaryKey="id"
          height={height}
          pagination={pagination}
          total={count}
        />
      </Stack>
      <NewBannerModal open={open} onClose={setOpen} />
    </Stack>
  );
};

export default PromoBanners;
